import React, { useEffect, useMemo, useState } from 'react';
import {
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HistoryIcon from '@mui/icons-material/History';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LinkIcon from '@mui/icons-material/Link';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CampaignIcon from '@mui/icons-material/Campaign';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import CodeIcon from '@mui/icons-material/Code';
import ChatIcon from '@mui/icons-material/Chat';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import WorkspaceBrandLogo from './WorkspaceBrandLogo';
import TenantSessionBadge from './TenantSessionBadge';
import NestedNavSection from './nav/NestedNavSection';
import { usePermissions } from '../hooks/usePermissions';
import { canAccessUsersSection, canAccessAiStudio, canAccessGameStudio, canAccessFiles, canAccessChatOperations, canAccessReports } from '../lib/userRoleHierarchy';
import { isAdminRole } from '../lib/rolePermissions';

export const OPERATIONS_TAB_IDS = new Set([
  'Monitoring',
  'Tickets',
  'ChatOperations',
  'ChatSettings',
  'CodeSandboxReview',
  'Activity',
]);
export const PRODUCT_TAB_IDS = new Set(['Portfolio', 'SprintPlanning', 'Kanban', 'Links']);
export const COMMS_TAB_IDS = new Set(['CommsOutreach']);
export const SETTINGS_TAB_IDS = new Set([
  'Workspace',
  'Users',
  'Notifications',
  'Profile',
  'SettingsBilling',
  'AiSpend',
  'TicketBriefings',
]);

const OPERATIONS_ITEMS = [
  { text: 'Uptime Monitoring', icon: <MonitorHeartIcon fontSize="small" />, id: 'Monitoring', permissionModule: 'monitoring' },
  { text: 'Tickets', icon: <ConfirmationNumberIcon fontSize="small" />, id: 'Tickets', permissionModule: 'ticketing' },
  {
    text: 'Chat',
    icon: <ChatIcon fontSize="small" />,
    id: 'ChatOperations',
    permissionModule: 'chatWidget',
    /** agent, manager, company_admin, super_admin (via chatWidget read permission). */
  },
  {
    text: 'Chat Settings',
    icon: <ChatIcon fontSize="small" />,
    id: 'ChatSettings',
    /** Visible to company_admin / super_admin only (not module-gated). */
    adminOnly: true,
  },
  { text: 'Code review', icon: <CodeIcon fontSize="small" />, id: 'CodeSandboxReview', permissionModule: 'codeReview' },
  { text: 'Activity', icon: <HistoryIcon fontSize="small" />, id: 'Activity', permissionModule: 'ticketing' },
];

const PRODUCT_ITEMS = [
  { text: 'Product Portfolio', icon: <WorkOutlineIcon fontSize="small" />, id: 'Portfolio', permissionModule: 'portfolio' },
  { text: 'Sprint Planning', icon: <AssignmentIcon fontSize="small" />, id: 'SprintPlanning', permissionModule: 'agile' },
  { text: 'Kanban Board', icon: <ViewColumnIcon fontSize="small" />, id: 'Kanban', permissionModule: 'agile' },
  { text: 'External Links', icon: <LinkIcon fontSize="small" />, id: 'Links', permissionModule: 'externalLinks' },
];

/**
 * @param {{ userData?: { role?: string, companyId?: string }, workspace?: object | null, activeTab: string, navigateToTab: (id: string) => void, setMobileOpen: (open: boolean) => void, drawerWidth: number }} props
 */
export default function Sidebar({
  userData,
  workspace,
  activeTab,
  navigateToTab,
  setMobileOpen,
  drawerWidth,
}) {
  const { checkPermission } = usePermissions(userData);
  const isCompanyAdmin = userData?.role === 'company_admin';
  const isSuperAdmin = userData?.role === 'super_admin';
  const canManageChatSettings = isCompanyAdmin || isSuperAdmin;
  const canViewAiSpend = isAdminRole(userData?.role);
  const hasCompany = Boolean(userData?.companyId);
  const showAiStudio = hasCompany && canAccessAiStudio(userData?.role, workspace);
  const showGameStudio = hasCompany && canAccessGameStudio(userData?.role, workspace);
  const showFiles = hasCompany && canAccessFiles(userData?.role, workspace);
  const showReports = hasCompany && canAccessReports(userData?.role, workspace);
  const showCrm =
    hasCompany && isCompanyAdmin && checkPermission('crm', 'read');

  const [openGroups, setOpenGroups] = useState({
    operations: OPERATIONS_TAB_IDS.has(activeTab),
    product: PRODUCT_TAB_IDS.has(activeTab),
    communications: COMMS_TAB_IDS.has(activeTab),
    settings: SETTINGS_TAB_IDS.has(activeTab),
  });

  useEffect(() => {
    setOpenGroups((prev) => ({
      operations: prev.operations || OPERATIONS_TAB_IDS.has(activeTab),
      product: prev.product || PRODUCT_TAB_IDS.has(activeTab),
      communications: prev.communications || COMMS_TAB_IDS.has(activeTab),
      settings: prev.settings || SETTINGS_TAB_IDS.has(activeTab),
    }));
  }, [activeTab]);

  const filterByPermission = (items) =>
    items.filter((item) => {
      if (item.adminOnly) return canManageChatSettings && hasCompany;
      if (item.id === 'ChatOperations') {
        if (!hasCompany || !canAccessChatOperations(userData?.role)) return false;
        if (!item.permissionModule) return true;
        return checkPermission(item.permissionModule, 'read');
      }
      if (!item.permissionModule) return true;
      return checkPermission(item.permissionModule, 'read');
    });

  const operationsChildren = useMemo(
    () =>
      filterByPermission(OPERATIONS_ITEMS).map((item) => ({
        id: item.id,
        text: item.text,
        icon: item.icon,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userData?.role, workspace, canManageChatSettings, hasCompany],
  );

  const productChildren = useMemo(
    () =>
      filterByPermission(PRODUCT_ITEMS).map((item) => ({
        id: item.id,
        text: item.text,
        icon: item.icon,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userData?.role, workspace],
  );

  const communicationsChildren = useMemo(() => {
    if (!isCompanyAdmin) return [];
    return [{ id: 'CommsOutreach', text: 'Team outreach', icon: <CampaignIcon fontSize="small" /> }];
  }, [isCompanyAdmin]);

  const settingsChildren = useMemo(() => {
    const items = [
      {
        id: 'Workspace',
        text: 'Workspace',
        icon: <BusinessIcon fontSize="small" />,
        visible: isCompanyAdmin,
      },
      {
        id: 'AiSpend',
        text: 'AI spend',
        icon: <SavingsOutlinedIcon fontSize="small" />,
        visible: hasCompany && canViewAiSpend,
      },
      {
        id: 'TicketBriefings',
        text: 'Ticket briefings',
        icon: <FactCheckOutlinedIcon fontSize="small" />,
        visible: hasCompany && canViewAiSpend,
      },
      {
        id: 'Users',
        text: 'Users & access',
        icon: <GroupsIcon fontSize="small" />,
        visible: hasCompany && canAccessUsersSection(userData?.role),
      },
      {
        id: 'Notifications',
        text: 'Notifications inbox',
        icon: <NotificationsIcon fontSize="small" />,
        visible: true,
      },
      {
        id: 'Profile',
        text: 'My profile',
        icon: <AccountCircleIcon fontSize="small" />,
        visible: true,
      },
      {
        id: 'SettingsBilling',
        text: 'Billing & plan',
        icon: <CreditCardOutlinedIcon fontSize="small" />,
        visible: isCompanyAdmin,
        secondary: 'Coming soon',
        disabled: true,
      },
    ];
    return items
      .filter((item) => item.visible)
      .map(({ id, text, icon, secondary, disabled }) => ({ id, text, icon, secondary, disabled }));
  }, [isCompanyAdmin, hasCompany, userData?.role, canViewAiSpend]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const goTo = (tabId) => {
    navigateToTab(tabId);
    setMobileOpen(false);
  };

  return (
    <div>
      <Toolbar
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 2,
          minHeight: 'auto !important',
          gap: 0.5,
        }}
      >
        <WorkspaceBrandLogo
          portalBrandingEnabled={Boolean(workspace?.portalBrandingEnabled)}
          logoUrl={workspace?.logoUrl}
          companyName={workspace?.name}
          maxWidth={drawerWidth - 20}
          sx={{ mx: 'auto' }}
        />
        <TenantSessionBadge userData={userData} workspace={workspace} variant="sidebar" />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textAlign: 'center', display: 'block' }}
        >
          {isCompanyAdmin
            ? 'Admin Portal'
            : userData?.role === 'developer'
              ? 'Developer'
              : userData?.role === 'manager'
                ? 'Manager'
                : 'Workspace'}
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton selected={activeTab === 'Overview'} onClick={() => goTo('Overview')}>
            <ListItemIcon sx={{ color: activeTab === 'Overview' ? 'primary.main' : 'inherit' }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Overview" />
          </ListItemButton>
        </ListItem>

        {showAiStudio && (
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 'AiStudio'}
              onClick={() => goTo('AiStudio')}
              sx={{
                borderLeft: activeTab === 'AiStudio' ? 3 : 0,
                borderColor: 'primary.main',
                bgcolor: activeTab === 'AiStudio' ? 'action.selected' : undefined,
              }}
            >
              <ListItemIcon sx={{ color: activeTab === 'AiStudio' ? 'primary.main' : 'inherit' }}>
                <AutoAwesomeIcon />
              </ListItemIcon>
              <ListItemText
                primary="AI Studio"
                primaryTypographyProps={{ fontWeight: activeTab === 'AiStudio' ? 700 : 600 }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {showGameStudio && (
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 'GameStudio'}
              onClick={() => goTo('GameStudio')}
              sx={{
                borderLeft: activeTab === 'GameStudio' ? 3 : 0,
                borderColor: 'primary.main',
                bgcolor: activeTab === 'GameStudio' ? 'action.selected' : undefined,
              }}
            >
              <ListItemIcon sx={{ color: activeTab === 'GameStudio' ? 'primary.main' : 'inherit' }}>
                <SportsEsportsIcon />
              </ListItemIcon>
              <ListItemText
                primary="AI Game Studio"
                primaryTypographyProps={{ fontWeight: activeTab === 'GameStudio' ? 700 : 600 }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {showGameStudio && (
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 'ImageEditor'}
              onClick={() => goTo('ImageEditor')}
              sx={{
                borderLeft: activeTab === 'ImageEditor' ? 3 : 0,
                borderColor: 'primary.main',
                bgcolor: activeTab === 'ImageEditor' ? 'action.selected' : undefined,
              }}
            >
              <ListItemIcon sx={{ color: activeTab === 'ImageEditor' ? 'primary.main' : 'inherit' }}>
                <ImageOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary="AI Image Editor"
                primaryTypographyProps={{ fontWeight: activeTab === 'ImageEditor' ? 700 : 600 }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {showFiles && (
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 'Files'}
              onClick={() => goTo('Files')}
              sx={{
                borderLeft: activeTab === 'Files' ? 3 : 0,
                borderColor: 'primary.main',
                bgcolor: activeTab === 'Files' ? 'action.selected' : undefined,
              }}
            >
              <ListItemIcon sx={{ color: activeTab === 'Files' ? 'primary.main' : 'inherit' }}>
                <FolderOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary="Files"
                primaryTypographyProps={{ fontWeight: activeTab === 'Files' ? 700 : 600 }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {showReports && (
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 'Reports'}
              onClick={() => goTo('Reports')}
              sx={{
                borderLeft: activeTab === 'Reports' ? 3 : 0,
                borderColor: 'primary.main',
                bgcolor: activeTab === 'Reports' ? 'action.selected' : undefined,
              }}
            >
              <ListItemIcon sx={{ color: activeTab === 'Reports' ? 'primary.main' : 'inherit' }}>
                <AssessmentOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary="Reports"
                primaryTypographyProps={{ fontWeight: activeTab === 'Reports' ? 700 : 600 }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {showCrm && (
          <ListItem disablePadding>
            <ListItemButton
              selected={activeTab === 'CrmMarketing'}
              onClick={() => goTo('CrmMarketing')}
              sx={{
                borderLeft: activeTab === 'CrmMarketing' ? 3 : 0,
                borderColor: 'primary.main',
                bgcolor: activeTab === 'CrmMarketing' ? 'action.selected' : undefined,
              }}
            >
              <ListItemIcon sx={{ color: activeTab === 'CrmMarketing' ? 'primary.main' : 'inherit' }}>
                <CampaignOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary="CRM & Marketing"
                primaryTypographyProps={{ fontWeight: activeTab === 'CrmMarketing' ? 700 : 600 }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {operationsChildren.length > 0 && (
          <NestedNavSection
            groupId="operations"
            label="Operations"
            icon={<PrecisionManufacturingIcon fontSize="small" />}
            open={openGroups.operations}
            onToggle={() => toggleGroup('operations')}
            sectionActive={OPERATIONS_TAB_IDS.has(activeTab)}
            activeTab={activeTab}
            children={operationsChildren}
            onSelect={goTo}
          />
        )}

        {productChildren.length > 0 && (
          <NestedNavSection
            groupId="product"
            label="Product & delivery"
            icon={<WorkOutlineIcon fontSize="small" />}
            open={openGroups.product}
            onToggle={() => toggleGroup('product')}
            sectionActive={PRODUCT_TAB_IDS.has(activeTab)}
            activeTab={activeTab}
            children={productChildren}
            onSelect={goTo}
          />
        )}

        {communicationsChildren.length > 0 && (
          <NestedNavSection
            groupId="communications"
            label="Communications"
            icon={<CampaignIcon fontSize="small" />}
            open={openGroups.communications}
            onToggle={() => toggleGroup('communications')}
            sectionActive={COMMS_TAB_IDS.has(activeTab)}
            activeTab={activeTab}
            children={communicationsChildren}
            onSelect={goTo}
          />
        )}

        {settingsChildren.length > 0 && (
          <NestedNavSection
            groupId="settings"
            label="Settings"
            icon={<SettingsOutlinedIcon fontSize="small" />}
            open={openGroups.settings}
            onToggle={() => toggleGroup('settings')}
            sectionActive={SETTINGS_TAB_IDS.has(activeTab)}
            activeTab={activeTab}
            children={settingsChildren}
            onSelect={goTo}
          />
        )}
      </List>
    </div>
  );
}
